import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotificationToast } from "../hooks";
import { StreamerContext } from "./StreamerContext";
import { StreamerRef, TargetWindow } from "./types";

export function StreamerContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const toast = useNotificationToast();
  const [isConnected, setIsConnected] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // per default, we want to enable the audio output
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [
    isAudioOutputBlockedByBrowserPolicy,
    setIsAudioOutputBlockedByBrowserPolicy,
  ] = useState(false);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = useState(true);
  const [windows, setWindows] = useState<TargetWindow[]>([]);
  const streamerRef = useRef<StreamerRef | null>(null);

  return (
    <StreamerContext.Provider
      value={{
        streamerRef,
        isAudioMuted,
        setIsAudioMuted,
        toggleAudioOutput: () => setIsAudioMuted((muted) => !muted),
        isAudioOutputBlockedByBrowserPolicy,
        setIsAudioOutputBlockedByBrowserPolicy,
        isMicrophoneMuted,
        toggleMicrophone: () => setIsMicrophoneMuted((muted) => !muted),
        isFullscreen,
        setIsFullscreen,
        toggleFullscreen: () => setIsFullscreen((fullscreen) => !fullscreen),
        onWindowChanged: (windowHandle: number) =>
          streamerRef.current?.onWindowChanged(windowHandle),
        copyToClipboard: () => streamerRef.current?.copyFromRemote(),
        pasteFromClipboard: async () => {
          try {
            const content = await navigator.clipboard.readText();
            streamerRef.current?.pasteToRemote(content);
          } catch (err) {
            toast({
              title: t("spectator.clipboard.error.missing-permission-title"),
              description: t(
                "spectator.clipboard.error.missing-permission-description",
              ),
              status: "warning",
            });
          }
        },
        windows,
        setWindows,
        isConnected,
        setIsConnected,
      }}
    >
      {children}
    </StreamerContext.Provider>
  );
}
